import React from 'react';
import { useModal } from '../contexts/ModalContext';
import ChatBox from '../chat/ChatBox';
import FeedbackModal from './FeedbackModal';
import PhoneWalkthrough from './PhoneWalkthrough';
import ChromeExtensionWalkthrough from './ChromeExtensionWalkthrough';
import ProductWalkthrough from './ProductWalkthrough';
import LinkedInConnectionsImport from './LinkedInConnectionsImport';
import OutlookEmailModal from './OutlookEmailModal';
import NotionImport from './NotionImport';

interface ModalRendererProps {
  location: { pathname: string };
}

const ModalRenderer: React.FC<ModalRendererProps> = ({ location }) => {
  const { activeModal, modalProps, closeModal } = useModal();

  if (!activeModal) return null;

  const renderModal = () => {
    switch (activeModal) {
      case 'chatBox':
        return (
          <ChatBox
            userId={modalProps.userId || ''}
            context={{
              currentRoute: location.pathname,
              selectedFounders: modalProps.context?.selectedFounders || [],
              activeTab: modalProps.context?.activeTab || null,
            }}
            onClose={() => {
              closeModal();
              if (location.pathname === '/app/tasks') {
                window.location.reload();
              }
            }}
            onRefreshFounders={modalProps.onRefreshFounders}
          />
        );

      case 'feedback':
        return <FeedbackModal onClose={closeModal} />;

      case 'phoneWalkthrough':
        return (
          <PhoneWalkthrough
            onClose={() => {
              if (modalProps.onComplete) {
                modalProps.onComplete();
              } else {
                closeModal();
              }
            }}
          />
        );

      case 'chromeExtensionWalkthrough':
        return (
          <ChromeExtensionWalkthrough
            onClose={() => {
              if (modalProps.onComplete) {
                modalProps.onComplete();
              } else {
                closeModal();
              }
            }}
          />
        );

      case 'productWalkthrough':
        return (
          <ProductWalkthrough
            onComplete={() => {
              if (modalProps.onComplete) {
                modalProps.onComplete();
              } else {
                closeModal();
              }
            }}
          />
        );

      case 'linkedInImport':
        return (
          <LinkedInConnectionsImport
            onComplete={(skipped?: boolean) => {
              if (modalProps.onComplete) {
                modalProps.onComplete(skipped);
              }
              closeModal();
            }}
          />
        );

      case 'outlookEmail':
        return (
          <OutlookEmailModal
            onClose={closeModal}
            connectedEmail={modalProps.connectedEmail}
          />
        );

      case 'notionImport':
        return (
          <NotionImport
            onComplete={(skipped?: boolean) => {
              if (modalProps.onComplete) {
                modalProps.onComplete(skipped);
              } else {
                closeModal();
              }
            }}
            initialStep={modalProps.initialStep}
          />
        );

      default:
        return null;
    }
  };

  return renderModal();
};

export default ModalRenderer;
